import { useMemo, useState } from "react";
import WithAuth from "../../hoc/WithAuth";
import { useCustomerList } from "../../api/customer";
import { useNavigate } from "react-router-dom";
import { AppButton } from "../../components/ui/AppButton";
import { DataTable } from "../../components/ui/data-table";
import { Icons } from "../../assets/images/icons";
import { ExportButton } from "../../components/ui/ExportButton";
import { useTablePrint } from "../../hooks/useTablePrint";
import type { Customer, CustomerListRow } from "../../types/customer";
import { formatDateCreatedValue } from "../../util/date-formatter";
import { Input } from "../../components/ui/input";
import { Button } from "../../components/ui/button";
import { ChevronLeft, ChevronRight } from "lucide-react";

const CustomerPage = () => {
  const navigate = useNavigate();
  const [searchText, setSearchText] = useState<string>("");
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(10);
  const { handlePrint } = useTablePrint();

  const { data: customers, isLoading } = useCustomerList(
    searchText,
    currentPage - 1,
    pageSize
  );

  const transformedCustomers = useMemo((): CustomerListRow[] => {
    if (!customers?.data?.content) return [];
    return customers.data.content.map((item: Customer) => ({
      id: String(item.id),
      dateCreated: item.dateCreated,
      idNumber: item.identificationNumber,
      fullName: item.fullName,
      phoneNumber: item.phoneNumber,
      tinNumber: item.tinNumber,
    }));
  }, [customers]);

  const handlePaginationChange = (page: number, size: number) => {
    setCurrentPage(page);
    setPageSize(size);
  };

  const handleSearch = (value: string) => {
    setSearchText(value);
    setCurrentPage(1);
  };

  const handleRowClick = (row: CustomerListRow) => {
    navigate(`/customers/${row.id}`);
  };

  const customerColumns = [
    {
      key: "idNumber",
      title: "ID Number",
      dataIndex: "idNumber",
      align: "left" as const,
    },
    {
      key: "fullName",
      title: "Full Name",
      dataIndex: "fullName",
      align: "left" as const,
    },
    {
      key: "phoneNumber",
      title: "Phone Number",
      dataIndex: "phoneNumber",
      align: "left" as const,
    },
    {
      key: "tinNumber",
      title: "TIN",
      dataIndex: "tinNumber",
      align: "left" as const,
    },
    {
      key: "dateCreated",
      title: "Date Created",
      dataIndex: "dateCreated",
      align: "left" as const,
      render: (_: unknown, record: CustomerListRow) =>
        formatDateCreatedValue(record.dateCreated),
    },
  ];

  const total = customers?.data?.totalElements ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  return (
    <>
      <div className="flex flex-col sm:flex-row justify-between gap-4">
        <div className="relative w-full lg:w-64">
          <Input
            value={searchText}
            onChange={(e) => handleSearch(e.target.value)}
            placeholder="Search farmers"
            className="pl-10 rounded-2xl w-full max-w-[308px] h-11"
          />
          <div className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400">
            <img src={Icons.search} alt="search" className="w-6 h-6" />
          </div>
        </div>

        <div className="flex gap-2 flex-wrap">
          <AppButton
            customIcon={Icons.printIcon}
            variant="outline"
            onClick={() =>
              handlePrint(customerColumns, transformedCustomers, "Customer Report")
            }
            className="bg-white text-gray-700 hover:bg-gray-50 border-gray-300"
          >
            Print
          </AppButton>

          <ExportButton
            columns={customerColumns}
            data={transformedCustomers}
            fileName="customers"
            title="Customer Report"
            pagination={{
              current: currentPage,
              pageSize: pageSize,
              total,
            }}
            customIcon={Icons.fileExport}
            className="bg-[#DDDDDD] hover:bg-gray-300"
          />
        </div>
      </div>

      {/* Mobile list (no farmer accounts on list page) */}
      <div className="md:hidden flex flex-col gap-3 mt-4">
        {isLoading ? (
          <div className="text-center text-gray-500 py-8">Loading...</div>
        ) : transformedCustomers.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 text-gray-500">
            <img
              src={Icons.noDataIcon}
              alt="No data"
              className="w-16 h-16 mb-2 opacity-60"
            />
            No farmers found
          </div>
        ) : (
          transformedCustomers.map((row) => (
            <button
              key={row.id}
              type="button"
              onClick={() => handleRowClick(row)}
              className="text-left rounded-xl border border-gray-200 bg-white p-4 shadow-sm active:bg-gray-50"
            >
              <div className="font-semibold text-gray-900">{row.fullName}</div>
              <div className="text-sm text-gray-600 mt-1">
                {row.phoneNumber} · ID {row.idNumber}
              </div>
              <div className="text-xs text-gray-500 mt-0.5">TIN {row.tinNumber}</div>
              <div className="text-xs text-gray-400 mt-1">
                Created {formatDateCreatedValue(row.dateCreated)}
              </div>
            </button>
          ))
        )}

        {total > 0 && (
          <div className="flex items-center justify-between pt-2 border-t border-gray-200">
            <span className="text-sm text-gray-600">
              Page {currentPage} of {totalPages}
            </span>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                type="button"
                onClick={() => handlePaginationChange(currentPage - 1, pageSize)}
                disabled={currentPage <= 1}
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <Button
                variant="outline"
                size="sm"
                type="button"
                onClick={() => handlePaginationChange(currentPage + 1, pageSize)}
                disabled={currentPage >= totalPages}
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        )}
      </div>

      <div className="hidden md:block mt-4">
        <DataTable<CustomerListRow>
          data={transformedCustomers}
          columns={customerColumns}
          loading={isLoading}
          pagination={{
            current: currentPage,
            pageSize: pageSize,
            total,
            onChange: handlePaginationChange,
          }}
          onRowClick={handleRowClick}
          emptyIcon={<img src={Icons.noDataIcon} alt="No data" />}
          emptyText="No farmers found"
          rowKey="id"
        />
      </div>
    </>
  );
};

export default WithAuth(CustomerPage);
